import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { formatCurrency } from '../utils/formatCurrency';

const MarkAsPaidDialog = ({ open, onOpenChange, invoice, onConfirm }) => {
  const { t } = useTranslation();
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [differenceReason, setDifferenceReason] = useState('');
  const [loading, setLoading] = useState(false);

  const invoiceTotal = invoice?.total_amount || 0;
  const paymentAmountNum = parseFloat(paymentAmount) || 0;
  const difference = Math.abs(invoiceTotal - paymentAmountNum);
  const hasDifference = difference > 0.01;

  useEffect(() => {
    if (open && invoice) {
      // Set default date to today
      const today = new Date().toISOString().split('T')[0];
      setPaymentDate(today);
      // Set default amount to invoice total
      setPaymentAmount(invoice.total_amount.toFixed(2));
      setDifferenceReason('');
    }
  }, [open, invoice]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentDate || !paymentAmount) {
      return;
    }

    if (hasDifference && !differenceReason.trim()) {
      return;
    }

    setLoading(true);
    try {
      await onConfirm({
        payment_date: new Date(paymentDate).toISOString(),
        payment_amount: paymentAmountNum,
        payment_difference_reason: hasDifference ? differenceReason : null,
        status: 'paid'
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error marking as paid:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('invoices.markAsPaid')}</DialogTitle>
          <DialogDescription>
            {t('invoices.markAsPaidDescription', { number: invoice?.invoice_number })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Invoice Amount Display */}
          <div className="p-3 bg-slate-50 rounded-sm border border-slate-200">
            <p className="text-xs text-slate-500">{t('invoices.invoiceAmount')}</p>
            <p className="font-mono text-lg font-bold text-slate-900">
              {formatCurrency(invoiceTotal)}
            </p>
          </div>

          {/* Payment Date */}
          <div className="space-y-2">
            <Label htmlFor="paymentDate">{t('invoices.paymentDate')} *</Label>
            <Input
              id="paymentDate"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              required
              className="rounded-sm"
            />
          </div>

          {/* Payment Amount */}
          <div className="space-y-2">
            <Label htmlFor="paymentAmount">{t('invoices.paymentAmount')} *</Label>
            <Input
              id="paymentAmount"
              type="number"
              step="0.01"
              min="0"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              required
              className="rounded-sm font-mono"
              placeholder="0.00"
            />
          </div>

          {/* Difference Warning and Reason */}
          {hasDifference && (
            <div className="space-y-2">
              <div className="p-3 bg-amber-50 rounded-sm border border-amber-200">
                <p className="text-sm text-amber-800 font-medium">
                  {t('invoices.paymentDifference')}: {formatCurrency(difference)}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  {paymentAmountNum < invoiceTotal 
                    ? t('invoices.underpayment')
                    : t('invoices.overpayment')}
                </p>
              </div>
              
              <Label htmlFor="differenceReason">{t('invoices.differenceReason')} *</Label>
              <Textarea
                id="differenceReason"
                value={differenceReason}
                onChange={(e) => setDifferenceReason(e.target.value)}
                required={hasDifference}
                className="rounded-sm"
                placeholder={t('invoices.differenceReasonPlaceholder')}
                rows={3}
              />
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={loading || (hasDifference && !differenceReason.trim())}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? t('common.loading') : t('invoices.confirmPayment')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MarkAsPaidDialog;
